import { atom, RecoilState, selector } from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import {
    Measurement,
    MeasurementDraft,
    MeasurementKind,
} from '~/domain/measurement/measurement';
import {
    AnnotationListState,
    annotationListState,
} from '~/state/atoms/annotationListAtoms';
import {
    intensityRegionDraftState,
    intensityRegionListState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';
import { MeasureFamily } from '~/utils/annotationKinds';

/** Recoil state for the measurement tool; the record model lives in the domain. */

/** The committed measurements, one atom each, oldest first. */
export const measurementListState =
    annotationListState<Measurement>('measurement');

/** The current in-progress placement, or null when idle. */
export const measurementDraftState = atom<MeasurementDraft | null>({
    key: 'measurementDraftState',
    default: null,
});

/** Which kind of measurement the next placement creates. */
export const measurementModeState = atom<MeasurementKind>({
    key: 'measurementModeState',
    default: 'distance',
});

/** The family last measured with, which Measure resumes. A UI preference. */
export const measureFamilyState = atom<MeasureFamily>({
    key: 'measureFamilyState',
    default: 'size',
});

/** Whether the Measure tray stays open while the pointer is elsewhere. */
export const measureTrayPinnedState = atom<boolean>({
    key: 'measureTrayPinnedState',
    default: false,
});

/** The lists Measure places into; RecoilState is invariant, hence the cast. */
export const MEASURE_ANNOTATION_LISTS = [
    measurementListState,
    lineProfileListState,
    intensityRegionListState,
] as unknown as readonly AnnotationListState<AnnotationRecord>[];

/** The drafts Measure places through; RecoilState is invariant, hence the cast. */
export const MEASURE_DRAFT_STATES = [
    measurementDraftState,
    lineProfileDraftState,
    intensityRegionDraftState,
] as unknown as readonly RecoilState<unknown>[];

/** Shapes placed, across all kinds. */
export const placedAnnotationCountState = selector<number>({
    key: 'placedAnnotationCountState',
    get: ({ get }) =>
        MEASURE_ANNOTATION_LISTS.flatMap((list) => get(list.ids)).length,
});

/** Distinct slices (series, orientation, plane) those shapes lie on. */
export const placedAnnotationSliceCountState = selector<number>({
    key: 'placedAnnotationSliceCountState',
    get: ({ get }) =>
        new Set(
            MEASURE_ANNOTATION_LISTS.flatMap((list) =>
                get(list.items).map(({ plane }) =>
                    [
                        plane.seriesInstanceUid,
                        plane.orientation,
                        ...plane.viewDirection,
                        plane.planeCoordinateMm,
                    ].join('|')
                )
            )
        ).size,
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/** True while a shape of any kind is being placed. */
export const annotationPlacementActiveState = selector<boolean>({
    key: 'annotationPlacementActiveState',
    get: ({ get }) => MEASURE_DRAFT_STATES.some((draft) => get(draft) !== null),
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});
